import type {
  HeaderAnalyticPayload,
  HeaderV4Props,
} from '@jouzen/ecom-components/layout';
import {
  defaultHeaderData,
  defaultHeaderSecondaryLinks,
  useHeaderContext,
} from '@jouzen/ecom-components/layout';
import { useRouter } from 'next/navigation';

import {
  DEFAULT_REFRESH_CART_VALUES,
  useCartContext,
} from '@/app/contexts/CartProvider';
import useCooperEnabled from '@/app/hooks/useCooperEnabled';
import useIsNomad2Enabled from '@/app/hooks/useIsNomad2Enabled';
import useIsNomadEnabled from '@/app/hooks/useIsNomadEnabled';
import useIsSteloEnabled from '@/app/hooks/useIsSteloEnabled';
import useUsaOnly from '@/app/hooks/useUsaOnly';

interface UseHeaderPropsOptions {
  currentPath: string;
  enableFadeAnimations: boolean;
  locale: string;
  onHamburgerClick: ({ open }: { open: boolean }) => void;
  onMenuLinkClick: (_: unknown, payload: HeaderAnalyticPayload) => void;
  parentHeaderProps?: Partial<HeaderV4Props> | undefined;
}

const useHeaderProps = ({
  currentPath,
  enableFadeAnimations,
  locale,
  onHamburgerClick,
  onMenuLinkClick,
  parentHeaderProps,
}: UseHeaderPropsOptions): HeaderV4Props => {
  const { inverse, fadeActions, fadeOuraLogo } = useHeaderContext()!;
  const router = useRouter();
  const { refreshCart } = useCartContext();
  const cart =
    refreshCart?.cart?.refreshCart.cart ??
    DEFAULT_REFRESH_CART_VALUES.cart!.refreshCart.cart!;

  const isSteloEnabled = useIsSteloEnabled();
  const isUSAOnly = useUsaOnly();
  const isNomadEnabled = useIsNomadEnabled();
  const isCooperEnabled = useCooperEnabled();
  const isNomad2Enabled = useIsNomad2Enabled();

  const handleShoppingCartClick = (): void => {
    router.push(`/${locale}/cart`);
  };

  const headerPosition: HeaderV4Props['headerPosition'] =
    parentHeaderProps?.headerPosition === 'static' ? 'static' : 'fold';

  return {
    count: cart.itemCountForIcon!,
    currentPath,
    enableFadeAnimations,
    fadeActions,
    fadeOuraLogo,
    headerConfig: defaultHeaderData({
      isCooperEnabled,
      isGiftCardEnabled: isUSAOnly,
      isNomadEnabled,
      isNomad2Enabled,
      isSteloEnabled,
    }),
    headerPosition,
    inverse,
    onHamburgerClick,
    onMenuLinkClick,
    onShoppingCartClick: handleShoppingCartClick,
    secondaryLinks: defaultHeaderSecondaryLinks,
    ...parentHeaderProps,
  };
};

export default useHeaderProps;
